import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import Icon from 'app/common/Icon/index';
import classes from './style.module.scss';
import { PeriodicCheckPayload } from "types/index";
import { useStoreState } from "~/store";
import List from "~/issues/List/List";
import moment from 'moment';
import { InspectionTypes } from "~/periodicCheck/store";
import TextDialog from '~/app/common/TextDialog';
import useTranslation from '~/app/hooks/useTranslation';
import ImageUpload from './ImageUpload';
import { PERIODIC_CHECK_ITEM_NOT_APPLICABLE } from '~/app/data/statuses/periodicCheck';
import { Languages } from '~/app/data/statuses/i18n';

export interface ListItemProps {
    item: PeriodicCheckPayload
    onSelect?: (item: any, status: PeriodicCheckPayload['status']) => void
    readOnly?: boolean
}

export type ListItemComponent = React.FC<ListItemProps>

const ListItem: ListItemComponent = ({ item, onSelect, readOnly }) => {

    const { t } = useTranslation("periodicCheck");
    const [...allIssues] = useStoreState(state => state.machine.data.issues);
    const lastOperatingTimeDate = useStoreState(state => state.machine.data.lastOperatingTimeDate);
    const lastOperatingCountDate = useStoreState(state => state.machine.data.lastOperatingCountDate);
    const operatingTimeAllowed = useStoreState(state => state.machine.data.operatingTimeAllowed);
    const operatingCountAllowed = useStoreState(state => state.machine.data.operatingCountAllowed);
    const inspectionType = useStoreState(state => state.periodicCheck.inspectionType);
    const language = useStoreState(state => state.user.data.language);
    const title = language === Languages.ENGLISH && item.nameEn?.trim() ? item.nameEn : item.name;
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [showImageUpload, setShowImageUpload] = useState<boolean>(false);
    const isPerodicCheckListEnabled = useMemo(() => {
        const isInspectionTypeRestricted = [InspectionTypes.DAILY, InspectionTypes.PERIODIC].includes(inspectionType);
        const isOperatingTimeAdded = !!operatingTimeAllowed && !moment(lastOperatingTimeDate).isSame(moment(), 'day');
        const isOperatingCountAdded = !!operatingCountAllowed && !moment(lastOperatingCountDate).isSame(moment(), 'day');
        return !(isInspectionTypeRestricted && (isOperatingTimeAdded || isOperatingCountAdded));
    }, [inspectionType, lastOperatingTimeDate, operatingTimeAllowed, lastOperatingCountDate, operatingCountAllowed]);

    const statusChangeHandler = useCallback((newStatus) => {
        if (readOnly || !onSelect || !isPerodicCheckListEnabled) {
            return;
        }
        if (item.isImageRequired && newStatus === 1 && !item?.images?.length) {
            setIsOpen(true);
            return;
        }
        onSelect(item, (newStatus === item.status) ? null : newStatus);
    }, [onSelect, isPerodicCheckListEnabled, item, readOnly]);

    const registerIssue = useCallback(() => {
        if (readOnly || !onSelect) {
            return;
        }
        onSelect(item, 2);
    }, [item, onSelect, readOnly]);

    const periodicCheckIssues = useMemo(() => {
        return allIssues.reverse().filter(issue => item.issues.map((issue) => issue.id.toString()).includes(issue.id.toString()));
    }, [item.issues, allIssues]);

    return (
        <>
            <div className={classes['list-item-wrapper']}>
                <div className={`${classes['list-item']} ${periodicCheckIssues.length ? classes['show-issues'] : ''}`}>
                    <div className={classes['title']}>{title} {item.isRequired && <span className={classes['required']}> *</span>}</div>
                    {(item.fluidTypeId && item.status !== null && item.status !== undefined && item.status !== PERIODIC_CHECK_ITEM_NOT_APPLICABLE) && <div className={classes['fluid-value']}>{item.quantity.toFixed(1)}</div>}
                    <div className={classNames(classes['icon-wrap'])}>
                        <Icon name={'image'}
                            className={`${classes['image']} ${item?.images?.length && classes['attached']}`}
                            onClick={() => !readOnly && isPerodicCheckListEnabled && setShowImageUpload(!showImageUpload)}
                        />
                        {!item.isRequired && (
                            <button
                                type="button"
                                className={`${classes['na-btn']} ${item.status === PERIODIC_CHECK_ITEM_NOT_APPLICABLE ? classes['checked'] : ''} ${(item.status === 1 || item.status === 2) ? classes['disabled'] : ''}`}
                                onClick={() => statusChangeHandler(PERIODIC_CHECK_ITEM_NOT_APPLICABLE)}
                            >
                                {t('notApplicable')}
                            </button>
                        )}
                        <Icon name={'close'}
                            className={`${classes['cancel']} ${item.status === 2 ? classes['checked'] : ''} ${(item.status === 1 || item.status === PERIODIC_CHECK_ITEM_NOT_APPLICABLE) ? classes['disabled'] : ''}`}
                            onClick={() => statusChangeHandler(2)}
                        />
                        <Icon name={'done'}
                            className={`${classes['ok']} ${item.status === 1 ? classes['checked'] : ''} ${(item.status === 2 || item.status === PERIODIC_CHECK_ITEM_NOT_APPLICABLE) ? classes['disabled'] : ''}`}
                            onClick={() => statusChangeHandler(1)}
                        />
                    </div>
                </div>
                <ImageUpload item={item} expanded={showImageUpload} statusChange={statusChangeHandler} />
            </div>
            {periodicCheckIssues.length && item.status === 2 ?
                <>
                    <List list={periodicCheckIssues} listClassName={'periodic-check-issues'} />
                    {!readOnly && <div className={classes['register-issue-link']} onClick={registerIssue}>{t('registerNewIssue')}</div>}
                </>
                : ''
            }
            <TextDialog
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                text={t('imageValidation')}
            />
        </>
    );
};

export default ListItem
